/**
 * Les lectures de la fiche d'un élève, celles de `StudentProfil.js`, à l'ouverture et à chaque
 * changement d'élève :
 *  - `getStudent` (`GET api/students/{id}`) : le réducteur recopie l'élève dans la liste du tuteur ; la
 *    réponse est aussi gardée ici, pour un élève qui n'y est pas (ouvert depuis l'historique des lives) ;
 *  - `getStudentBrief` (`GET api/students/{id}/brief`, `objective_id` et `live_occurrence_id` quand la
 *    route les porte) ; le brief n'atteint pas le store ;
 *  - `student_brief.opened` (`context`, `student_id`, `has_objective`), à chaque lecture du brief.
 *
 * Les blocs d'admin (« Santé élève », `getStudentHealth`) ne sont pas repris : ils ne s'affichaient qu'à
 * `ability.can("read", "backOffice")`, jamais à un tuteur ni en impersonation (audit, A1).
 *
 * La fiche montée cachée (au large, rien d'ouvert) ne lit rien : `studentId` nul.
 *
 * La tranche du brief, que la salle du live lit aussi, est rendue au repos après une erreur
 * (`idleOnError`) : `useApiFetch` garde l'échec, le squelette du brief s'arrête. Un élève illisible a sa
 * page d'erreur et « Réessayer ».
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useEffect, useMemo } from "react"

import { studentBriefService } from "../../../../../../data/services/studentBriefService"
import { studentsService } from "../../../../../../data/services/studentsService"
import {
    type ApiTutorStudent,
    type ApiTutorStudentBrief,
} from "../../../../adapters/tutor/Students/tutorStudentAdapter"
import { useApiFetch } from "../../../hooks/useApiFetch"
import { type TutorStudentId } from "./tutorStudentTabs"

export type TutorStudentBriefParams = {
    objectiveId?: string | number | null
    liveOccurrenceId?: string | number | null
    /** `context` de l'événement : « profile » hors live (le défaut de l'ancienne fiche). */
    context?: string | null
}

/** Les paramètres du brief, ceux de l'ancienne fiche : seulement ceux que la route porte. */
function briefUrlParams(params: TutorStudentBriefParams): Record<string, string | number> {
    const out: Record<string, string | number> = {}
    if (params.objectiveId) out.objective_id = params.objectiveId
    if (params.liveOccurrenceId) out.live_occurrence_id = params.liveOccurrenceId
    return out
}

/** La mission du champion jointe à la lecture de l'élève : seule la colonne du champion de « Mes élèves » la lit. */
export const WITH_SAM_MISSION = { sam_mission: 1 }

export function useTutorStudentProfileData(
    studentId: TutorStudentId | null,
    params: TutorStudentBriefParams = {},
    withSamMission = false
) {
    const { objectiveId = null, liveOccurrenceId = null, context = "profile" } = params
    const key = studentId == null ? null : `${studentId}:${objectiveId ?? ""}:${liveOccurrenceId ?? ""}`
    const routeParams = useMemo(() => (studentId == null ? undefined : { id: studentId }), [studentId])
    const urlParams = useMemo(() => briefUrlParams({ objectiveId, liveOccurrenceId }), [objectiveId, liveOccurrenceId])

    const student = useApiFetch<ApiTutorStudent>({
        service: studentsService.getStudent,
        routeParams,
        urlParams: withSamMission ? WITH_SAM_MISSION : undefined,
        requestKey: studentId == null ? null : String(studentId),
        enabled: studentId != null,
        // L'erreur reste sur la tranche : l'onglet Activités la lit aussi (le crochet la rend au repos au
        // démontage). L'élève, lui, est au store.
    })
    const brief = useApiFetch<ApiTutorStudentBrief>({
        service: studentBriefService.getStudentBrief,
        routeParams,
        urlParams,
        requestKey: key,
        enabled: studentId != null,
        idleOnError: true,
    })

    useEffect(() => {
        if (studentId == null) return
        amplitude.track("student_brief.opened", {
            context: context ?? "profile",
            student_id: studentId,
            has_objective: !!objectiveId,
        })
        // Une fois par lecture du brief : à l'ouverture et à chaque changement d'élève.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key])

    return {
        raw: student.raw,
        studentError: student.error,
        retryStudent: student.fetch,
        brief: brief.raw,
        /** Le brief n'est pas encore là, et n'a pas échoué : son squelette (`isBriefLoading`). */
        briefLoading: brief.loading,
    }
}
